import { Node, mergeAttributes } from '@tiptap/core'
import { Plugin } from '@tiptap/pm/state'
import { storeToRefs } from 'pinia'

import { useEditorStore } from '@/stores'
import { DynamicNodeAttrs, VariableNodeAttrs } from '@/types'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    variableNode: {
      insertVariable: (attrs: VariableNodeAttrs) => ReturnType
      openVariableDialog: (attrs?: VariableNodeAttrs, callback?: (attrs: DynamicNodeAttrs) => void) => ReturnType
    }
  }
}

const variableNodeName = 'variableNode'

export const VariableNode = Node.create({
  name: variableNodeName,
  group: 'inline',
  inline: true,
  atom: true,
  selectable: false,

  addAttributes() {
    return {
      name: {
        default: null,
        parseHTML: element => element.getAttribute('data-name'),
        renderHTML: attributes => {
          if (!attributes.name) return {}
          return { 'data-name': attributes.name }
        },
      },
      filter: {
        default: null,
        parseHTML: element => element.getAttribute('data-filter'),
        renderHTML: attributes => {
          if (!attributes.filter) return {}
          return { 'data-filter': attributes.filter }
        },
      },
      filterArg: {
        default: null,
        parseHTML: element => element.getAttribute('data-filter-arg'),
        renderHTML: attributes => {
          if (!attributes.filterArg) return {}
          return { 'data-filter-arg': attributes.filterArg }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-variable="true"]',
        priority: 100,
      },
      {
        tag: 'span[data-name]',
        priority: 50,
      },
    ]
  },

  renderHTML({ node, HTMLAttributes }) {
    let content = node.attrs.name

    if (node.attrs.filter) {
      content += `|${node.attrs.filter}`
      if (node.attrs.filterArg) content += `("${node.attrs.filterArg}")`
    }
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-variable': 'true',
        'data-name': node.attrs.name,
        ...(node.attrs.filter && { 'data-filter': node.attrs.filter }),
        ...(node.attrs.filterArg && { 'data-filter-arg': node.attrs.filterArg }),
      }),
      `{{ ${content} }}`,
    ]
  },

  addCommands() {
    const editor = this.editor
    return {
      insertVariable:
        attrs =>
        ({ chain }) => {
          return chain()
            .insertContent({
              type: this.name,
              attrs,
            })
            .run()
        },
      openVariableDialog: (attrs, callback) => () => {
        const editorStore = useEditorStore()
        const { dynamicDialogType, dynamicDialogAttrs, dynamicDialogCallback } = storeToRefs(editorStore)
        dynamicDialogType.value = 'variable'
        dynamicDialogAttrs.value = attrs
        dynamicDialogCallback.value = callback ?? (attrs => editor?.commands.insertVariable(attrs as VariableNodeAttrs))
        return true
      },
    }
  },
  addProseMirrorPlugins() {
    const editor = this.editor
    return [
      new Plugin({
        props: {
          handleClickOn(view, _pos, node, nodePos) {
            if (node.type.name !== variableNodeName || !editor.isEditable) return false
            editor?.commands.openVariableDialog(node.attrs as VariableNodeAttrs, attrs => {
              view.dispatch(view.state.tr.setNodeMarkup(nodePos, undefined, attrs))
            })
            return true
          },
        },
      }),
    ]
  },
})
